import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useState } from "react";
import { useLangStore } from "@/lib/langStore";
import { translations } from "@/lib/i18n";
import { SERVICE_TIERS, FAQS } from "@/lib/index";
import Layout from "@/components/Layout";
import { NavLink } from "react-router-dom";
import { ROUTE_PATHS } from "@/lib/index";

export default function ServicesPage() {
  const { lang } = useLangStore();
  const t = translations[lang];
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-primary font-semibold text-sm uppercase tracking-wider mb-2"
            >
              ✦ {lang === "en" ? "Pricing" : "الأسعار"}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl lg:text-5xl font-extrabold text-foreground mb-3"
            >
              {t.services.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-muted-foreground text-lg max-w-xl mx-auto"
            >
              {t.services.subtitle}
            </motion.p>
          </div>

          {/* Pricing cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            {SERVICE_TIERS.map((tier, i) => {
              const titleKey = tier.titleKey as keyof typeof t.services;
              const descKey = tier.descKey as keyof typeof t.services;
              const title = t.services[titleKey] as string;
              const desc = t.services[descKey] as string;
              const features = lang === "ar" ? tier.features.ar : tier.features.en;

              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className={`relative rounded-2xl border-2 ${tier.color} bg-card p-7 flex flex-col transition-all duration-300 ${
                    tier.popular ? "shadow-2xl shadow-primary/15 scale-[1.02] border-primary" : "hover:border-primary/50"
                  }`}
                >
                  {tier.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold shadow-lg shadow-primary/30">
                        ✦ {t.services.popular}
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-xl font-extrabold text-foreground mb-1">{title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{desc}</p>
                    <div className="flex items-end gap-1">
                      <span className="text-4xl font-extrabold text-foreground">{tier.price}</span>
                      <span className="text-muted-foreground text-sm mb-1">
                        {lang === "en" ? "/ project" : "/ مشروع"}
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <Check
                          size={16}
                          className={`shrink-0 mt-0.5 ${tier.popular ? "text-primary" : "text-muted-foreground"}`}
                        />
                        <span className="text-sm text-foreground/80">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <NavLink
                    to={ROUTE_PATHS.CONTACT}
                    className={`w-full py-3 rounded-xl font-semibold text-sm text-center transition-all duration-200 active:scale-95 ${
                      tier.popular
                        ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25"
                        : "bg-muted text-foreground hover:bg-muted/80 border border-border"
                    }`}
                  >
                    {tier.popular ? t.services.getStarted : t.services.contactUs}
                  </NavLink>
                </motion.div>
              );
            })}
          </div>

          {/* Additional Services */}
          <div className="mb-20">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl font-extrabold text-foreground text-center mb-10"
            >
              ✦ {t.services.additionalServices}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: t.services.socialPost,
                  price: "150 - 200 EGP",
                },
                {
                  title: t.services.namingBrand,
                  price: "200 - 350 EGP",
                },
                {
                  title: t.services.fullSocialPackage,
                  price: "300 - 600 EGP",
                },
              ].map((service, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl border border-border bg-card/50 flex flex-col items-center text-center group hover:border-primary/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
                    <Check size={24} />
                  </div>
                  <h3 className="font-bold text-foreground mb-2">{service.title}</h3>
                  <p className="text-primary font-extrabold">{service.price}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="max-w-2xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl font-extrabold text-foreground text-center mb-8"
            >
              {t.services.faqTitle}
            </motion.h2>
            <div className="space-y-3">
              {FAQS.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="rounded-xl border border-border bg-card overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-start hover:bg-muted/30 transition-colors"
                  >
                    <span className="font-semibold text-foreground text-sm">
                      {lang === "ar" ? faq.questionAr : faq.questionEn}
                    </span>
                    <span className={`text-primary transition-transform duration-200 ${openFaq === i ? "rotate-45" : ""}`}>
                      +
                    </span>
                  </button>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed border-t border-border pt-4"
                    >
                      {lang === "ar" ? faq.answerAr : faq.answerEn}
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
